import React, { useMemo } from "react";
import { Navigate } from "react-router";
import { VelavuPermission } from "velavu-js-api";
import { useSignInContext } from "./sign-in-context";

export interface RequirePermissionProps {
	permissions: readonly VelavuPermission[];
	redirectTo?: string;
	children: React.ReactNode;
}

/**
 * Redirects away when the user lacks any of the required permissions.
 * Renders children only when every permission resolves to true.
 */
export function RequirePermission(props: RequirePermissionProps) {
	const { hasPermission } = useSignInContext();
	const { permissions, redirectTo = "/" } = props;

	const hasPermissionArray = useMemo(() => {
		return permissions.map((permission) => hasPermission(permission));
	}, [hasPermission, permissions]);

	const resolved = useMemo(() => {
		return hasPermissionArray.every((value) => value !== undefined);
	}, [hasPermissionArray]);

	const allowed = useMemo(
		() => hasPermissionArray.every((value) => value === true),
		[hasPermissionArray],
	);

	if (!resolved) {
		//Don't render anything until the permissions are resolved
		return null;
	} else if (!allowed) {
		//Redirect if we are not allowed
		return <Navigate to={redirectTo} replace />;
	} else {
		return props.children;
	}
}
